/// <reference path="../../../types/axios-fix.d.ts" />
import { BaseService, httpClientService, authInterceptorService, errorHandlerService, cacheService } from '../core';
import type { ApiResponse } from '../core/error-handler.service';

/**
 * Content Search Parameters
 */
export interface ContentSearchParams {
  query?: string;
  organization_id: string;
  team_id?: string;
  status?: string[];
  authors?: string[];
  mime_types?: string[];
  tag_group_filters?: Record<string, string[]>;
  system_tags?: string[];
  personal_tags?: string[];
  created_after?: string;
  created_before?: string;
  updated_after?: string;
  updated_before?: string;
  min_rating?: number;
  max_rating?: number;
  min_file_size?: number;
  max_file_size?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  page?: number;
  limit?: number;
  fuzzy_search?: boolean;
  include_archived?: boolean;
  exact_match?: boolean;
  search_fields?: string[];
  bookmarked_only?: boolean;
  personal_tags_only?: boolean;
  system_tags_only?: boolean;
  user_permissions?: string[]; // User's permissions for RBAC-based filtering
}


/**
 * Search Suggestions Parameters
 */
export interface SearchSuggestionsParams {
  query: string;
  organization_id: string;
  team_id?: string;
  types?: string[];
  limit?: number;
}

/**
 * Tag Search Parameters
 */
export interface TagSearchParams {
  query?: string;
  organization_id: string;
  team_id?: string;
  tag_group_id?: string;
  limit?: number;
  include_usage_stats?: boolean;
}

/**
 * Saved Search Parameters
 */
export interface SavedSearchParams {
  organization_id: string;
  team_id?: string;
  visibility?: string;
}

/**
 * Saved Search Data
 */
export interface SavedSearchData {
  name: string;
  description?: string;
  search_params: ContentSearchParams;
  visibility?: 'private' | 'organization' | 'team';
  organization_id: string;
  team_id?: string;
  is_default?: boolean;
  schedule?: {
    enabled: boolean;
    frequency: string;
    time?: string;
    timezone?: string;
  };
  notifications?: {
    enabled: boolean;
    email?: boolean;
    in_app?: boolean;
    threshold?: number;
  };
}

/**
 * Search Service
 * 
 * Handles all search-related operations including:
 * - Content search with facets and filters
 * - Search suggestions
 * - Tag search
 * - Saved searches management
 * 
 * Extracted from the monolithic API service.
 */
export class SearchService extends BaseService {
  constructor() {
    super(httpClientService, authInterceptorService, errorHandlerService, cacheService);
  }

  /**
   * Search content with advanced filters and facets
   */
  async searchContent(params: ContentSearchParams): Promise<ApiResponse<any[]>> {
    console.log('Searching content with params:', params);

    // Build the request body directly instead of using URLSearchParams
    // This preserves arrays properly for the POST request
    const requestBody: any = {};
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value) && value.length > 0) {
          // Keep arrays as arrays
          requestBody[key] = value;
        } else if (typeof value === 'object') {
          // For objects like tag_group_filters, keep as-is
          requestBody[key] = value;
        } else if (value !== '') {
          // Add non-empty primitive values
          requestBody[key] = value;
        }
      }
    });

    console.log('Sending search request with body:', requestBody);

    const response = await this.requestWithMetadata<any>(
      'POST',
      '/content/search',
      requestBody,
      { timeout: 15000 }
    );

    console.log(`Found ${response.metadata?.pagination?.totalCount || response.data?.length || 0} content items`);
    return response;
  }

  /**
   * Get search suggestions based on partial query
   */
  async getSearchSuggestions(params: SearchSuggestionsParams): Promise<{
    suggestions: Array<{
      type: string;
      value: string;
      label: string;
      count?: number;
      metadata?: any;
    }>;
  }> {
    console.log('Getting search suggestions for:', params.query);

    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach(v => queryParams.append(key, v.toString()));
        } else {
          queryParams.append(key, value.toString());
        }
      }
    });

    const response = await this.get<any>(
      `/search/suggestions?${queryParams}`,
      { timeout: 5000 }
    );

    return response;
  }

  /**
   * Search tags with filtering
   */
  async searchTags(params: TagSearchParams): Promise<{
    tags: Array<{
      id: string;
      name: string;
      tag_group_id: string;
      tag_group_name: string;
      usage_count?: number;
      last_used?: string;
    }>;
    total_count: number;
  }> {
    console.log('Searching tags with params:', params);

    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString());
      }
    });

    const response = await this.get<any>(
      `/tags/search?${queryParams}`,
      { timeout: 5000 }
    );

    return response;
  }

  /**
   * Search unified tags (personal and organizational)
   */
  async searchUnifiedTags(params: {
    query?: string;
    organization_id: string;
    team_id?: string;
    tag_type?: 'personal' | 'organizational' | 'all';
    tag_group_id?: string;
    limit?: number;
    include_usage_stats?: boolean;
  }): Promise<{
    tags: Array<{
      id: string;
      name: string;
      type: 'personal' | 'organizational';
      tag_group_id?: string;
      tag_group_name?: string;
      usage_count?: number;
      last_used?: string;
      created_by?: string;
      created_at?: string;
    }>;
    total_count: number;
  }> {
    console.log('Searching unified tags with params:', params);

    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString());
      }
    });

    const response = await this.get<any>(
      `/tags/unified/search?${queryParams}`,
      { timeout: 5000 }
    );

    return response;
  }

  /**
   * Get saved searches
   */
  async getSavedSearches(params: SavedSearchParams): Promise<any[]> {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString());
      }
    });

    const response = await this.get<any[]>(
      `/saved-searches?${queryParams}`
    );

    return response || [];
  }

  /**
   * Get a specific saved search by ID
   */
  async getSavedSearch(id: string): Promise<any> {
    const response = await this.get<any>(`/saved-searches/${id}`);
    return response;
  }

  /**
   * Create a new saved search
   */
  async createSavedSearch(data: SavedSearchData): Promise<any> {
    const response = await this.post<any>('/saved-searches', data);
    
    // Invalidate search cache
    this.invalidateCache('search');
    
    return response;
  }

  /**
   * Update an existing saved search
   */
  async updateSavedSearch(id: string, data: Partial<SavedSearchData>): Promise<any> {
    const response = await this.put<any>(`/saved-searches/${id}`, data);
    
    // Invalidate search cache
    this.invalidateCache('search');
    
    return response;
  }

  /**
   * Delete a saved search
   */
  async deleteSavedSearch(id: string): Promise<void> {
    await this.delete<void>(`/saved-searches/${id}`);
    
    // Invalidate search cache
    this.invalidateCache('search');
  }

  /**
   * Duplicate a saved search
   */
  async duplicateSavedSearch(id: string, data: { name: string }): Promise<any> {
    const response = await this.post<any>(
      `/saved-searches/${id}/duplicate`, 
      data
    );
    
    // Invalidate search cache
    this.invalidateCache('search');
    
    return response;
  }

  /**
   * Execute a saved search
   */
  async executeSavedSearch(id: string, overrides?: Partial<ContentSearchParams>): Promise<ApiResponse<any[]>> {
    const response = await this.requestWithMetadata<any[]>(
      'POST',
      `/saved-searches/${id}/execute`,
      overrides || {},
      { timeout: 15000 }
    );
    
    return response;
  }

  /**
   * Get saved search statistics
   */
  async getSavedSearchStats(id: string, params?: { 
    start_date?: string; 
    end_date?: string 
  }): Promise<{
    execution_count: number;
    average_results: number;
    last_executed?: string;
    popular_filters?: Record<string, any>;
    performance?: {
      average_time_ms: number;
      min_time_ms: number;
      max_time_ms: number;
    };
  }> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });
    }

    const response = await this.get<any>(
      `/saved-searches/${id}/stats?${queryParams}`
    );
    
    return response;
  }

  /**
   * Share a saved search with users
   */
  async shareSavedSearch(id: string, data: { user_ids: string[] }): Promise<void> {
    await this.post<void>(`/saved-searches/${id}/share`, data);
  }

  /**
   * Get popular search terms
   */
  async getPopularSearchTerms(params: {
    organization_id: string;
    team_id?: string;
    time_range?: 'day' | 'week' | 'month' | 'year';
    limit?: number;
  }): Promise<Array<{
    term: string;
    count: number;
    trend?: 'up' | 'down' | 'stable';
  }>> {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString());
      }
    });

    const response = await this.get<any>(
      `/search/popular-terms?${queryParams}`
    );
    
    return response || [];
  }

  /**
   * Get search history for the current user
   */
  async getSearchHistory(params: {
    limit?: number;
    offset?: number;
  }): Promise<Array<{
    query: string;
    timestamp: string;
    result_count: number;
    filters?: Record<string, any>;
  }>> {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.offset) queryParams.append('offset', params.offset.toString());

    const response = await this.get<any>(
      `/search/history?${queryParams}`
    );
    
    return response || [];
  }

  /**
   * Clear search history
   */
  async clearSearchHistory(): Promise<void> {
    await this.delete<void>('/search/history');
  }
}

// Export singleton instance
export const searchService = new SearchService();